/**
 * @file index.tsx
 *
 * @fileoverview The context that stores and updates bookmarked jobs.
 */

import React, { createContext, useEffect, useRef, useState } from 'react';
import { Job } from '~/jobiqo/types/types';

export interface BookmarkedJobsContextInterface {
  /**
   * The current bookmarked jobs.
   */
  bookmarkedJobs: Job[];
  /**
   * Allows setting currently bookmarked jobs.
   */
  setBookmarkedJobs: React.Dispatch<React.SetStateAction<Job[]>>;
  /**
   * Returns the bookmarked job by id.
   * Initially, this is undefined and only gets defined once the context mounts.
   * That's why we can use this to check if the context did mount.
   */
  getBookmarkedJob?: (id: number) => Job | null;
}

export const BookmarkedJobsContext = createContext<
  BookmarkedJobsContextInterface
>({
  bookmarkedJobs: [],
  setBookmarkedJobs: () => {},
});

const BookmarkedJobsProvider = ({ children, initialState }) => {
  const [bookmarkedJobs, setBookmarkedJobs] = useState<Job[]>(initialState);
  const previousInitialIdRef = useRef<string>(
    JSON.stringify(initialState.map((job: Job) => job.id).sort()),
  );

  const getBookmarkedJob = (id: number) =>
    bookmarkedJobs.find((job) => job.id === id) ?? null;

  // Only reset from initialState when the set of bookmarked job IDs change, not
  // on every re-render.
  useEffect(() => {
    const newIds = JSON.stringify(
      initialState.map((job: Job) => job.id).sort(),
    );
    if (newIds !== previousInitialIdRef.current) {
      previousInitialIdRef.current = newIds;
      setBookmarkedJobs(initialState);
    }
  }, [initialState]);

  return (
    <BookmarkedJobsContext.Provider
      value={{ bookmarkedJobs, setBookmarkedJobs, getBookmarkedJob }}
    >
      {children}
    </BookmarkedJobsContext.Provider>
  );
};

export default BookmarkedJobsProvider;
